import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaVideo, FaGlobe, FaLightbulb } from "react-icons/fa";
import { AppCard, AppButton, IconBox } from "../components/app";
import { appColors, appType, appRadius } from "../utils/appTheme";
import { entrarPublico, fechaMontevideo, horaMontevideo } from "../utils/asambleas/api";
import { IDIOMA_POR_DEFECTO, fechaLargaAsamblea, idiomaSoportado } from "../utils/asambleas/idioma";

/**
 * Enlace personal de ingreso a una asamblea por Zoom (/z/:t), sin iniciar
 * sesión: llega por WhatsApp o email 15 minutos antes (plantilla
 * asamblea_ingreso). Pide el acceso con POST (el GET del endpoint no tiene
 * efectos, así las vistas previas de WhatsApp no lo usan) y ofrece dos
 * botones: abrir la app de Zoom o entrar desde el navegador. Si la asamblea
 * pasó a link manual (proveedor "manual"), un solo botón: "Abrir la
 * videollamada" (puede ser Meet o Teams: no se habla de la app de Zoom). Si
 * todavía no se habilitó, dice a qué hora (de Montevideo) y vuelve a probar
 * solo a esa hora. No muestra datos de nadie más: solo el acceso de quien
 * tiene el enlace. El acceso no se guarda (ni storage ni logs).
 *
 * Idioma: el del propietario si el servidor lo manda en la respuesta
 * (`idioma`: es | en | pt-BR); si no, español. Nunca el del navegador ni el
 * que quedó guardado en el dispositivo (i18n.language).
 */

const MAX_TIMEOUT_MS = 2147483647;
// Errores con texto propio (los demás: "No pudimos preparar tu acceso").
const ESTADOS_ERROR = new Set(["vencido", "no_encontrado", "terminada", "cancelada", "no_disponible", "sin_video", "sin_acceso"]);
// Con estos se ofrece "Volver a intentar" ("no_disponible" no: el módulo está
// apagado y reintentar no lo arregla).
const ESTADOS_REINTENTABLES = new Set(["error", "sin_acceso", "sin_video"]);
// Margen después de la hora de habilitación antes de volver a pedir el acceso.
const MARGEN_REINTENTO_MS = 1500;
// Si el servidor dice "todavía" con una hora ya pasada (relojes desfasados), no
// se insiste más seguido que esto.
const REINTENTO_MINIMO_MS = 30000;

export default function EntrarAsamblea() {
  const { t: token } = useParams();
  const { i18n } = useTranslation("asambleas");
  // cargando | listo | todavia | vencido | no_encontrado | terminada | cancelada | no_disponible | sin_video | sin_acceso | error
  const [estado, setEstado] = useState("cargando");
  const [acceso, setAcceso] = useState(null); // { joinUrl, webUrl, proveedor } (solo en memoria)
  const [asamblea, setAsamblea] = useState(null); // datos propios de la asamblea si el servidor los manda
  const [habilitaEnMs, setHabilitaEnMs] = useState(null);
  // El último que mandó el servidor (se mantiene al reintentar).
  const [idioma, setIdioma] = useState(IDIOMA_POR_DEFECTO);
  const t = useMemo(() => i18n.getFixedT(idioma, "asambleas"), [i18n, idioma]);
  const montado = useRef(true);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const pedir = useCallback(async () => {
    setEstado("cargando");
    const res = await entrarPublico(token);
    if (!montado.current) return;
    const idiomaRes = idiomaSoportado(res.idioma);
    if (idiomaRes) setIdioma(idiomaRes);
    if (res.joinUrl) {
      setAcceso({ joinUrl: res.joinUrl, webUrl: res.webUrl || null, proveedor: res.proveedor || null });
      setAsamblea(res.asamblea || null);
      setEstado("listo");
    } else if (res.todavia) {
      setHabilitaEnMs(res.habilitaEnMs);
      setEstado("todavia");
    } else {
      setEstado(ESTADOS_ERROR.has(res.error) ? res.error : "error");
    }
  }, [token]);

  useEffect(() => {
    pedir();
  }, [pedir]);

  // "Todavía no empezó": vuelve a pedir el acceso solo, a la hora en que se habilita.
  useEffect(() => {
    if (estado !== "todavia" || !habilitaEnMs) return undefined;
    const faltan = habilitaEnMs - Date.now();
    const espera = faltan > 0 ? faltan + MARGEN_REINTENTO_MS : REINTENTO_MINIMO_MS;
    const timer = setTimeout(() => pedir(), Math.min(espera, MAX_TIMEOUT_MS));
    return () => clearTimeout(timer);
  }, [estado, habilitaEnMs, pedir]);

  const contenedor = { minHeight: "100vh", background: appColors.cream, display: "flex", justifyContent: "center", padding: "24px 16px" };
  const pie = (
    <div style={{ ...appType.body, fontSize: 12, color: appColors.ink3, textAlign: "center", marginTop: 16 }}>
      {t("zoomPublico.personal")}
    </div>
  );

  if (estado === "cargando" || estado === "error" || ESTADOS_ERROR.has(estado)) {
    const textos = {
      cargando: t("zoomPublico.cargando"),
      vencido: t("zoomPublico.vencido"),
      no_encontrado: t("zoomPublico.noEncontrado"),
      terminada: t("zoomPublico.terminada"),
      cancelada: t("zoomPublico.cancelada"),
      no_disponible: t("zoomPublico.noDisponible"),
      sin_video: t("zoomPublico.sinLink"),
      sin_acceso: t("zoomPublico.sinAcceso"),
      error: t("zoomPublico.error"),
    };
    return (
      <main style={contenedor} lang={idioma}>
        <div style={{ maxWidth: 520, width: "100%", textAlign: "center", paddingTop: 48 }}>
          <div role={estado === "cargando" ? "status" : "alert"} style={{ ...appType.body, color: appColors.ink3 }}>
            {textos[estado]}
          </div>
          {ESTADOS_REINTENTABLES.has(estado) && (
            <div style={{ marginTop: 16 }}>
              <AppButton variant="outline" onClick={pedir}>
                {t("zoomPublico.reintentar")}
              </AppButton>
            </div>
          )}
        </div>
      </main>
    );
  }

  const nombreEdificio = asamblea && asamblea.edificio && asamblea.edificio.nombre ? asamblea.edificio.nombre : "";
  // Link manual: el que pegó la administración (Zoom, Meet, Teams…), el mismo para todos.
  const manual = !!acceso && acceso.proveedor === "manual";
  let textoTodavia = "";
  if (estado === "todavia") {
    if (!habilitaEnMs) textoTodavia = t("zoomPublico.todaviaSinHora");
    else if (fechaMontevideo(habilitaEnMs) === fechaMontevideo(Date.now())) {
      textoTodavia = t("zoomPublico.todavia", { hora: horaMontevideo(habilitaEnMs) });
    } else {
      textoTodavia = t("zoomPublico.todaviaOtroDia", { fecha: fechaLargaAsamblea(habilitaEnMs, idioma), hora: horaMontevideo(habilitaEnMs) });
    }
  }

  return (
    <main style={contenedor} lang={idioma}>
      <div style={{ maxWidth: 520, width: "100%" }}>
        <AppCard padding={0} style={{ overflow: "hidden" }}>
          <div style={{ padding: "18px 18px 0", display: "flex", gap: 12, alignItems: "flex-start" }}>
            <IconBox icon={<FaVideo />} color={appColors.blue} size={44} />
            <div style={{ flex: 1, minWidth: 0 }}>
              {nombreEdificio && <div style={{ ...appType.statLabel, color: appColors.blue, marginBottom: 3 }}>{nombreEdificio}</div>}
              <h1 style={{ ...appType.cardTitle, fontSize: 17, margin: 0, fontWeight: 400 }}>{t("zoomPublico.titulo")}</h1>
              <div style={{ ...appType.body, color: appColors.ink3, marginTop: 2 }}>
                {estado !== "listo" ? t("zoomPublico.subtituloTodavia") : manual ? t("zoomPublico.listoManual") : t("zoomPublico.listo")}
              </div>
            </div>
          </div>

          <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 10 }}>
            {estado === "todavia" && (
              <>
                <div role="status" style={{ background: appColors.cream, borderRadius: appRadius.tile, padding: "12px 14px", ...appType.body, color: appColors.ink }}>
                  {textoTodavia}
                </div>
                <AppButton variant="outline" block onClick={pedir}>
                  {t("zoomPublico.reintentar")}
                </AppButton>
              </>
            )}
            {estado === "listo" && acceso && manual && (
              <>
                <a href={acceso.joinUrl} rel="noopener noreferrer" style={{ ...estiloBoton, ...estiloPrimario }}>
                  <FaVideo size={14} aria-hidden="true" /> {t("zoomPublico.abrir")}
                </a>
                <div style={{ ...appType.body, fontSize: 12.5, color: appColors.ink3, marginTop: -4 }}>{t("zoomPublico.ayudaManual")}</div>
              </>
            )}
            {estado === "listo" && acceso && !manual && (
              <>
                <a href={acceso.joinUrl} rel="noopener noreferrer" style={{ ...estiloBoton, ...estiloPrimario }}>
                  <FaVideo size={14} aria-hidden="true" /> {t("zoomPublico.app")}
                </a>
                <div style={{ ...appType.body, fontSize: 12.5, color: appColors.ink3, marginTop: -4 }}>{t("zoomPublico.ayudaApp")}</div>
                {acceso.webUrl && (
                  <>
                    <a href={acceso.webUrl} rel="noopener noreferrer" style={{ ...estiloBoton, ...estiloSecundario }}>
                      <FaGlobe size={14} aria-hidden="true" /> {t("zoomPublico.navegador")}
                    </a>
                    <div style={{ ...appType.body, fontSize: 12.5, color: appColors.ink3, marginTop: -4 }}>{t("zoomPublico.ayudaNavegador")}</div>
                  </>
                )}
              </>
            )}
            <div style={{ ...appType.body, fontSize: 13, color: appColors.ink2, display: "flex", alignItems: "flex-start", gap: 8, marginTop: 4 }}>
              <FaLightbulb style={{ color: appColors.amber, fontSize: 13, marginTop: 2, flexShrink: 0 }} aria-hidden="true" />
              <span>{t("zoomPublico.consejo")}</span>
            </div>
          </div>
        </AppCard>
        {pie}
      </div>
    </main>
  );
}

const estiloBoton = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  width: "100%",
  boxSizing: "border-box",
  padding: "14px 16px",
  borderRadius: appRadius.button,
  fontFamily: appType.fontFamily,
  fontSize: 15,
  fontWeight: 400,
  lineHeight: 1,
  textDecoration: "none",
  border: "1px solid transparent",
  transition: "background .15s ease, border-color .15s ease",
};

const estiloPrimario = { background: appColors.blue, color: appColors.onBlue };
const estiloSecundario = { background: "transparent", color: appColors.ink, borderColor: appColors.line };
